import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getTranslatedExercise } from '../../translationService';
import { getExerciseAnimationUrl } from '../../utils/exerciseImages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExerciseDetailModal = ({ exercise, onClose, language, t }) => {
  const [translatedExercise, setTranslatedExercise] = useState(exercise);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!exercise?.id) return;
      // The list payload is slim — fetch FULL detail (execution steps, muscles,
      // benefits) for the single exercise being viewed.
      let full = exercise;
      try {
        const res = await axios.get(`${API}/exercises/${exercise?.id}`);
        full = { ...exercise, ...res.data };
      } catch (e) {
        console.error('Failed to load full exercise:', e);
      }
      if (cancelled) return;

      if (language === 'en') {
        setTranslatedExercise(full);
        return;
      }
      setIsTranslating(true);
      try {
        const translated = await getTranslatedExercise(exercise?.id, language, full);
        if (!cancelled) setTranslatedExercise({ ...full, ...translated });
      } catch (error) {
        console.error('Translation error:', error);
        if (!cancelled) setTranslatedExercise(full);
      } finally {
        if (!cancelled) setIsTranslating(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [exercise?.id, language]);

  if (!exercise) return null;
  const view = translatedExercise || exercise;

  return (
    <div 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        overflowY: 'auto'
      }} 
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: '#FFFFFF',
          borderColor: '#3B82F6',
          borderWidth: '4px',
          borderStyle: 'solid',
          borderRadius: '1rem',
          padding: '2rem',
          maxWidth: '56rem',
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          position: 'relative',
          margin: 'auto'
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
              {view.name}
              {isTranslating && <span style={{ fontSize: '0.875rem', color: '#6B7280', marginLeft: '0.5rem' }}>🔄</span>}
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <span style={{ backgroundColor: '#3B82F6', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '0.25rem', fontSize: '0.875rem' }}>{t(view.category)}</span>
              {view.difficulty && (
                <span style={{
                  backgroundColor: view.difficulty === 'beginner' ? '#10B981' : view.difficulty === 'intermediate' ? '#F59E0B' : '#EF4444',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {t(view.difficulty)}
                </span>
              )}
              {view.equipment && (
                <span style={{ backgroundColor: '#4B5563', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem' }}>
                  {view.equipment}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ color: '#4B5563', fontSize: '2rem', fontWeight: 'bold', lineHeight: '1', border: 'none', background: 'none', cursor: 'pointer' }}>
            ×
          </button>
        </div>

        {/* Exercise Image/GIF */}
        {(view.animation_webp || view.image_url) && (
          <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            <img 
              src={getExerciseAnimationUrl(view)} 
              alt={view.name}
              style={{ width: '100%', maxWidth: '28rem', margin: '0 auto', borderRadius: '0.5rem' }}
            />
          </div>
        )}

        {/* Muscle Information - Show detailed if available, otherwise show simple */}
        {(() => {
          const hasDetailedBreakdown = (view.target_muscles?.length > 0) || 
                                       (view.synergist_muscles?.length > 0) || 
                                       (view.stabilizer_muscles?.length > 0);
          
          if (hasDetailedBreakdown) {
            // Show Detailed Muscle Breakdown
            return (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.75rem' }}>💪 {t('muscles_worked')}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {view.target_muscles && view.target_muscles.length > 0 && (
                    <div>
                      <p style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>🎯 {t('target_muscles')}:</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {view.target_muscles.map((muscle, idx) => (
                          <span key={idx} style={{ backgroundColor: '#EF4444', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500' }}>
                            {muscle}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {view.synergist_muscles && view.synergist_muscles.length > 0 && (
                    <div>
                      <p style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>🤝 {t('synergist_muscles')}:</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {view.synergist_muscles.map((muscle, idx) => (
                          <span key={idx} style={{ backgroundColor: '#F59E0B', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500' }}>
                            {muscle}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {view.stabilizer_muscles && view.stabilizer_muscles.length > 0 && (
                    <div>
                      <p style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>🔒 {t('stabilizer_muscles')}:</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {view.stabilizer_muscles.map((muscle, idx) => (
                          <span key={idx} style={{ backgroundColor: '#10B981', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500' }}>
                            {muscle}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          } else if (view.muscle_groups && view.muscle_groups.length > 0) {
            // Fallback to simple muscle groups
            return (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.75rem' }}>💪 {t('muscles_worked')}</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {view.muscle_groups.map((muscle, idx) => (
                    <span key={idx} style={{ backgroundColor: '#8B5CF6', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500' }}>
                      {t(muscle.toLowerCase().replace(/\s+/g, '_')) || muscle}
                    </span>
                  ))}
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* 1. How to Perform (Execution Steps) - FIRST */}
        {view.execution_steps && view.execution_steps.length > 0 && (
          <div style={{ marginBottom: '1.5rem', backgroundColor: '#F0FDF4', padding: '1rem', borderRadius: '0.5rem', borderLeft: '4px solid #10B981' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#166534', marginBottom: '0.75rem' }}>🎯 {t('how_to_perform') || 'How to Perform'}</h3>
            <ol style={{ paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', margin: 0 }}>
              {view.execution_steps.map((step, idx) => (
                <li key={idx} style={{ color: '#374151', fontSize: '0.9rem', lineHeight: '1.6' }}>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* 2. Common Mistakes - WARNING STYLE */}
        {view.common_mistakes && view.common_mistakes.length > 0 && (
          <div style={{ marginBottom: '1.5rem', backgroundColor: '#FEF2F2', padding: '1rem', borderRadius: '0.5rem', borderLeft: '4px solid #EF4444' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#991B1B', marginBottom: '0.75rem' }}>⚠️ {t('common_mistakes')}</h3>
            <ul style={{ paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', margin: 0 }}>
              {view.common_mistakes.map((mistake, idx) => (
                <li key={idx} style={{ color: '#991B1B', fontSize: '0.9rem', lineHeight: '1.5' }}>
                  {mistake}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 3. Benefits - EDUCATIONAL STYLE */}
        {view.benefits && view.benefits.length > 0 && (
          <div style={{ marginBottom: '1.5rem', backgroundColor: '#EFF6FF', padding: '1rem', borderRadius: '0.5rem', borderLeft: '4px solid #3B82F6' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1E40AF', marginBottom: '0.75rem' }}>💎 {t('benefits')}</h3>
            <ul style={{ paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', margin: 0 }}>
              {view.benefits.map((benefit, idx) => (
                <li key={idx} style={{ color: '#1E40AF', fontSize: '0.9rem', lineHeight: '1.5' }}>
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Form Tips (secondary, less prominent) */}
        {view.form_tips && view.form_tips.length > 0 && (
          <div style={{ marginBottom: '1.5rem', backgroundColor: '#FFFBEB', padding: '1rem', borderRadius: '0.5rem', borderLeft: '4px solid #F59E0B' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#B45309', marginBottom: '0.5rem' }}>✨ {t('form_tips')}</h3>
            <ul style={{ paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', margin: 0 }}>
              {view.form_tips.map((tip, idx) => (
                <li key={idx} style={{ color: '#92400E', fontSize: '0.85rem', lineHeight: '1.4' }}>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        <button 
          onClick={onClose} 
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: '#3B82F6',
            color: 'white',
            borderRadius: '0.5rem',
            border: 'none',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          {t('close')}
        </button>
      </div>
    </div>
  );
};

// Client Exercise Browser Component

export default ExerciseDetailModal;
